import React, { useState, useEffect, useCallback } from "react";
import axios from "../../../api/apiClient";
import { domain, token } from "../../Security";

// Common Components
import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage,
} from "../../../common/APICommon";
import { LoadingSpinner } from "../../../common/Loading";

// Icons
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

// Logic / Constants
import { initialAccessRightsWithTypes } from "../../../constants/accessRights";
import { SetAuditTrail, SetAuditTrailBulk } from "../../Reports/AuditTrailReportEmployeeSetup";
import { AddViewUpdateAccessTemplateModal } from "../forms/AddViewUpdateAccessTemplateModal";

export function UserTemplate({ isOpen, onClose, user }) {
  // Use the centralized message hook matching Client.js
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [userTemplate, setUserTemplate] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [filterDataIndex, setFilterDataIndex] = useState(0);
  const [searchPlaceholder, setSearchPlaceholder] = useState("template name");

  // Logic States (Preserved core functionality)
  const [formAccessRightsData, setFormAccessRightsData] = useState({
    id: null,
    description: "",
    userTimeOut: "",
    accessRights: JSON.parse(JSON.stringify(initialAccessRightsWithTypes)),
  });

  const [selectedData, setSelectedData] = useState(null);
  const [oldDataReference, setOldDataReference] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const apiUrl = domain + "/api/AccessTemplate";
  const headers = ["Id", "Template Name", "Actions"];
  const accessors = ["id", "description", "actions"];

  const resetLocalVariables = () => {
    setFormAccessRightsData({
      id: null,
      description: "",
      userTimeOut: "",
      accessRights: JSON.parse(JSON.stringify(initialAccessRightsWithTypes)),
    });
    setSelectedData(null);
    setOldDataReference(null);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setUserTemplate(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (isOpen) fetchData();
  }, [fetchData, isOpen]);

  useEffect(() => {
    setSearchPlaceholder(filterDataIndex === 1 ? "template id" : "template name");
  }, [filterDataIndex]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    if (!value) {
      setUserTemplate(filterData);
      return;
    }
    const res = filterData.filter((f) => {
      if (filterDataIndex === 1) return f.id.toString().startsWith(value);
      return f.description.toLowerCase().includes(lowercaseValue);
    });
    setUserTemplate(res);
  };

  // --- CORE LOGIC (UNTOUCHED) ---
  const buildBaseAddUpdateDataWithAccessModes = (formData) => {
    const { description, userTimeOut, accessRights } = formData;
    const flattenedRights = Object.values(accessRights).flat().reduce((acc, curr) => {
      if (curr.key) acc[curr.key] = !!curr.value;
      return acc;
    }, {});
    const accessModes = Object.values(accessRights).flat().map((item) => {
      const findValue = (key) => item.accessType?.find((t) => t.key === key)?.value ?? false;
      return {
        accessTemplateKey: item.key,
        createMode: findValue("createMode"),
        editMode: findValue("editMode"),
        deleteMode: findValue("deleteMode"),
        viewMode: findValue("viewMode"),
      };
    });
    return {
      description,
      userTimeOut: userTimeOut !== "" ? Number(userTimeOut) : null,
      ...flattenedRights,
      accessModes,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoadingRequest(true);
      const baseData = buildBaseAddUpdateDataWithAccessModes(formAccessRightsData);
      await axios.post(apiUrl, baseData, { headers: { 'Authorization': `Bearer ${token}` } });
      await SetAuditTrail({
        setupArea: "Users", actionTaken: "insert", field: "Add New Template",
        newValue: `${baseData.description}`, oldValue: "-",
        dateSaved: new Date(), savedById: user.id,
      });
      await fetchData();
      showGeneralMessage("Template created successfully!", true);
      setShowAddForm(false);
      resetLocalVariables();
    } catch (error) {
      showGeneralMessage(error.response?.data?.message || "Failed to create", false);
    } finally { setLoadingRequest(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoadingRequest(true);
      const baseData = buildBaseAddUpdateDataWithAccessModes(formAccessRightsData);

      let auditInfo = [];
      const oldData = oldDataReference;
      const newData = baseData;
      const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

      allKeys.forEach((key) => {
        if (key === "accessModes") {
          newData.accessModes?.forEach((mode, idx) => {
            const oldMode = oldData.accessModes?.[idx] || {};
            ["createMode", "editMode", "deleteMode", "viewMode"].forEach((flag) => {
              if ((oldMode[flag] ?? false) !== (mode[flag] ?? false)) {
                auditInfo.push({
                  setupArea: "Users", actionTaken: "update",
                  field: `${mode.accessTemplateKey} - ${flag}`,
                  newValue: (mode[flag] ?? false).toString(),
                  oldValue: (oldMode[flag] ?? false).toString(),
                  dateSaved: new Date(), savedById: user.id,
                });
              }
            });
          });
        } else if (oldData[key] !== newData[key] && key !== "hasReports" && key !== "id") {
          auditInfo.push({
            setupArea: "Users", actionTaken: "update",
            field: key,
            newValue: newData[key]?.toString() ?? "",
            oldValue: oldData[key]?.toString() ?? "",
            dateSaved: new Date(), savedById: user.id,
          });
        }
      });

      await axios.put(`${apiUrl}/${selectedData.id}`, baseData, { headers: { 'Authorization': `Bearer ${token}` } });
      if (auditInfo.length > 0) await SetAuditTrailBulk({ data: auditInfo });
      await fetchData();
      showGeneralMessage("Template updated successfully!", true);
      setShowEditForm(false);
      resetLocalVariables();
    } catch (error) {
      showGeneralMessage(error.response?.data?.message || "Update failed", false);
    } finally { setLoadingRequest(false); }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);
    setOldDataReference(data);
    setShowEditForm(true);
  };

  const actions = [{
    name: "Edit",
    tooltip: "Edit Template Rights",
    icon: <EditIcon fontSize="small" />,
    className: "bg-green-500 hover:bg-green-600 flex items-center",
    function: editHandleClick,
    visible: true,
  }];

  // Preserved mapping logic for hydration
  useEffect(() => {
    if (showEditForm && selectedData) {
      const updatedRights = Object.fromEntries(
        Object.entries(formAccessRightsData.accessRights).map(([category, rightsArray]) => [
          category,
          rightsArray.map((item) => {
            const am = selectedData.accessModes?.find((m) => m.accessTemplateKey === item.key) || {};
            return {
              ...item,
              value: !!selectedData[item.key],
              accessType: [
                { key: "createMode", name: "Create", value: !!am.createMode },
                { key: "editMode", name: "Change", value: !!am.editMode },
                { key: "deleteMode", name: "Delete", value: !!am.deleteMode },
                { key: "viewMode", name: "View", value: !!am.viewMode },
              ],
            };
          }),
        ])
      );
      setFormAccessRightsData(prev => ({
        ...prev,
        id: selectedData.id,
        description: selectedData.description,
        userTimeOut: selectedData.userTimeOut,
        accessRights: updatedRights,
      }));
    }
  }, [selectedData, showEditForm]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-3 text-gray-700">
      <div className="bg-gray-50 w-full md:w-[75%] lg:w-[60%] rounded-lg shadow-xl h-auto max-h-[95vh] flex flex-col relative overflow-hidden">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
            <span className="text-xl text-blue-600">User Template Management</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow space-y-4">
          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-4">

            {/* Filter and Search Section */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500">Filter:</span>
              <select
                className="border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-500 outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                onChange={(e) => setFilterDataIndex(e.target.value === "ID" ? 1 : 0)}
              >
                <option value="All">Template Name</option>
                <option value="ID">Template Id</option>
              </select>
            </div>
            <div className="flex flex-col md:flex-row justify-between my-3 py-2 gap-2">
              <SearchBar placeholder={"Search by " + searchPlaceholder} handleFilter={handleFilter} parentClassName="!m-0 w-[70%] md:w-[50%]"/>
              <Button
                icon={<AddIcon />}
                name={"Add Template"}
                className="bg-green-500 hover:bg-green-600 border-none cursor-pointer text-sm "
                onClick={() => setShowAddForm(true)}
              />

            </div>

            {/* Table Section */}
            <div className="overflow-x-auto">
              <CreateTable
                tableName={"UserTemplate"}
                headers={headers}
                accessors={accessors}
                data={userTemplate}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={userTemplate?.length}
                emptyMessage={loading ? "Loading data..." : "No templates found"}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-5 border-t border-gray-200 bg-white rounded-b-lg">
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded border-none cursor-pointer transition-colors shadow-md">
            Close
          </button>
        </div>

        {/* Feature Modals */}
        <AddViewUpdateAccessTemplateModal
          visible={showAddForm}
          onClose={() => { setShowAddForm(false); resetLocalVariables(); }}
          onSave={handleSubmit}
          modalName={"Add Access Template"}
          data={formAccessRightsData}
          setData={setFormAccessRightsData}
        />

        <AddViewUpdateAccessTemplateModal
          visible={showEditForm}
          onClose={() => { setShowEditForm(false); resetLocalVariables(); }}
          onSave={handleUpdate}
          modalName={"Edit Access Template"}
          data={formAccessRightsData}
          setData={setFormAccessRightsData}
        />

        <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
        <LoadingSpinner loading={loadingRequest || loading} />
      </div>
    </div>
  );
}